import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import React from "react";

const MonthView = ({ date, tasks, onTaskClick }) => {
  // Get the first day of the month
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  // Get the last day of the month
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDayOfMonth.getDay();

  // Get the total number of days in the month
  const totalDays = lastDayOfMonth.getDate();

  // Create an array of days to display (including days from previous/next month)
  const days = [];

  // Add days from previous month
  const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 0);
  const prevMonthDays = prevMonth.getDate();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({
      day: prevMonthDays - i,
      isCurrentMonth: false,
      date: new Date(date.getFullYear(), date.getMonth() - 1, prevMonthDays - i),
    });
  }

  // Add days from current month
  for (let i = 1; i <= totalDays; i++) {
    days.push({
      day: i,
      isCurrentMonth: true,
      date: new Date(date.getFullYear(), date.getMonth(), i),
    });
  }

  // Add days from next month
  const remainingDays = 42 - days.length; // 6 rows * 7 days = 42
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      day: i,
      isCurrentMonth: false,
      date: new Date(date.getFullYear(), date.getMonth() + 1, i),
    });
  }

  // Get tasks for a specific day
  const getTasksForDay = (dayDate) => {
    return tasks.filter((task) => {
      const taskDate = new Date(task.updated_at);
      return (
        taskDate.getDate() === dayDate.getDate() && taskDate.getMonth() === dayDate.getMonth() && taskDate.getFullYear() === dayDate.getFullYear()
      );
    });
  };

  // Helper to get task status color
  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "bg-secondary";
      case "In Progress":
        return "bg-primary";
      case "Review":
        return "bg-warning";
      case "Done":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  // Get developer names for display
  const getDeveloperNames = (developers) => {
    if (!developers || developers.length === 0) {
      return "Unassigned";
    }

    if (developers.length === 1) {
      const dev = developers[0];
      return dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `ID: ${dev.id}`;
    }

    const firstDev = developers[0];
    const firstName = firstDev.name || `${firstDev.first_name || ""} ${firstDev.last_name || ""}`.trim() || `ID: ${firstDev.id}`;
    return `${firstName} +${developers.length - 1} more`;
  };

  // Format date for display
  const formattedMonth = date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  // Day names for the header
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="month-view">
      <h3 className="month-header">{formattedMonth}</h3>

      <div className="calendar-grid">
        {/* Day names header */}
        <div className="calendar-header">
          {dayNames.map((dayName) => (
            <div key={dayName} className="day-name">
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="calendar-body">
          {days.map((day, index) => {
            const dayTasks = getTasksForDay(day.date);
            const isToday =
              day.date.getDate() === new Date().getDate() &&
              day.date.getMonth() === new Date().getMonth() &&
              day.date.getFullYear() === new Date().getFullYear();

            return (
              <div key={index} className={`calendar-day ${!day.isCurrentMonth ? "other-month" : ""} ${isToday ? "today" : ""}`}>
                <div className="day-number">{day.day}</div>
                <div className="day-tasks">
                  {dayTasks.slice(0, 3).map((task) => (
                    <div
                      key={task.id}
                      className={`task-item ${getStatusColor(task.status)}`}
                      onClick={() => onTaskClick(task)}
                      id={`task-${task.id}`}
                    >
                      <Tooltip target={`#task-${task.id}`} content={task.description} />
                      <div className="task-title">{task.title}</div>
                      <div className="task-meta">
                        <div className="task-developer">{getDeveloperNames(task.developers)}</div>
                        <Tag
                          value={task.status}
                          severity={
                            task.status === "To Do"
                              ? "secondary"
                              : task.status === "In Progress"
                              ? "info"
                              : task.status === "Review"
                              ? "warning"
                              : "success"
                          }
                          size="small"
                        />
                      </div>
                    </div>
                  ))}
                  {dayTasks.length > 3 && <div className="more-tasks">+{dayTasks.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .month-view {
          padding: 1rem;
        }
        
        .month-header {
          margin-bottom: 1.5rem;
          text-align: center;
          font-weight: 600;
          color: #333;
        }
        
        .calendar-grid {
          display: flex;
          flex-direction: column;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
        }
        
        .calendar-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          background-color: #f5f5f5;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .day-name {
          padding: 10px;
          text-align: center;
          font-weight: 600;
          color: #666;
        }
        
        .calendar-body {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          grid-template-rows: repeat(6, 1fr);
        }
        
        .calendar-day {
          min-height: 120px;
          border-right: 1px solid #e0e0e0;
          border-bottom: 1px solid #e0e0e0;
          padding: 5px;
        }
        
        .calendar-day:nth-child(7n) {
          border-right: none;
        }
        
        .other-month {
          background-color: #f9f9f9;
          color: #999;
        }
        
        .today {
          background-color: #e6f7ff;
        }
        
        .day-number {
          font-weight: 500;
          margin-bottom: 5px;
        }
        
        .day-tasks {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .task-item {
          padding: 5px 8px;
          border-radius: 4px;
          cursor: pointer;
          color: white;
          font-size: 0.8rem;
          transition: transform 0.2s;
        }
        
        .task-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .task-title {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .task-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.7rem;
        }
        
        .task-developer {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 70%;
        }
        
        .more-tasks {
          text-align: center;
          font-size: 0.8rem;
          color: #666;
          padding: 2px;
          background-color: #f0f0f0;
          border-radius: 4px;
          cursor: pointer;
        }
        
        .more-tasks:hover {
          background-color: #e0e0e0;
        }
      `}</style>
    </div>
  );
};

export default MonthView;
